import {h, Component} from 'preact'
import _ from 'underscore'
import {HomepageElement} from '../homepage'
import {isHtmlElement} from '../../libs/preact-entry'
import HomepageEditor from './editor'
import OrderAnimator from './orderAnimator'
import Portal from 'preact-portal'
import NewWidgetButton, {TypesThatCanBeAdded, widgetsToAddFullWidth, widgetsToAddMainContent, widgetsToAddSidebar} from './new-button'
import {arrayMove} from 'react-sortable-hoc'
import {MovePayload, DonePayload} from './toolbar'
import Tracker from '../tracker/Tracker'
import Mover from '../tracker/Mover'
import { publish, subscribe } from '../../utils/globals'
import {getDefaultWidget} from './default-configurations'
import generator from '../../utils/idGenerator'

interface ColumnProps {
  classPrefix: string,
  container: string,
  controlUrl: string,
  onChildMouseOver?: () => void,
  onChildMouseOut?: () => void,
  elements: Array<HomepageElement>,
  widgetList?: Array<widgetsToAddSidebar | widgetsToAddFullWidth | widgetsToAddMainContent>,
  onChange: (els: Array<HomepageElement>) => void,
  nested: boolean,
  currentPage: 'homepage' | 'ideation'
}

interface ColumnState {
  editingId: number | string | null,
  buttonIndex: number,
  disableButtonHover?: boolean,
  x: number,
  y: number,
  newButtonPosition: {
    x: number,
    y: number,
    width: number
  },
  disabled: boolean,
  isNewButtonVisible: boolean
}

const nonMovableElements = ['footer', 'header', 'navigation_bar']

export default class WidgetColumn extends Component<ColumnProps, ColumnState> {
  columnContainer: Element | null
  minX: number
  minY: number
  maxX: number
  maxY: number
  setConstraintsThrottled: () => void
  idGenerator: () => number
  containerCache: {[key: number]: HTMLElement | undefined | null} = {}

  constructor(props: ColumnProps) {
    super()
    this.state = {
      editingId: null,
      disabled: false,
      newButtonPosition: {
        x: 0,
        y: 0,
        width: 0
      },
      buttonIndex: props.elements.length,
      x: 0,
      y: 0,
      isNewButtonVisible: false
    }
    this.columnContainer = null
    this.minX = 0
    this.minY = 0
    this.maxX = Infinity
    this.maxY = Infinity
    this.onDone = this.onDone.bind(this)
    this.onRemove = this.onRemove.bind(this)
    this.onAddWidget = this.onAddWidget.bind(this)
    this.newButtonToggle = this.newButtonToggle.bind(this)
    this.setConstraints = this.setConstraints.bind(this)
    this.onEdit = this.onEdit.bind(this)
    this.onMove = this.onMove.bind(this)
    this.onClose = this.onClose.bind(this)
    this.onVisibleToggle = this.onVisibleToggle.bind(this)
    this.setConstraintsThrottled = _.debounce(this.setConstraints, 200)
    this.idGenerator = generator(props.elements.length)

    subscribe('edit_column', (payload) => {
      this.setState({disabled: payload.editing})
    })
  }

  onMove(positionToMoveUp: number, {direction}: MovePayload) {
    if (direction === 'down') {
      positionToMoveUp += 1
    }
    this.props.onChange(arrayMove(this.props.elements, positionToMoveUp, positionToMoveUp - 1))
  }

  onDone(change: DonePayload) {
    this.setState({
      editingId: null
    }, () => publish('edit_column', {editing: false})
    )
    this.props.onChange(
      this.props.elements.map((el, index) => {
        if (index !== change.idx) return el
        if (change.element.type !== el.type) return el
        return Object.assign({}, el, change.element)
      })
    )
  }

  onVisibleToggle(idx: number, visible: boolean) {
    const elements = this.props.elements.map((el, index) => {
      if (idx !== index) return el
      return {
        ...el,
        active: visible
      }
    })
    this.props.onChange(elements)
  }

  onRemove(removedIndex: number) {
    const elements = this.props.elements.filter((_, index) => removedIndex !== index)
    this.props.onChange(elements)
  }

  onAddWidget(type: TypesThatCanBeAdded) {
    const parentSelector = `${this.props.container}`
    const parent = document.querySelector(parentSelector)
    if (!parent) throw new Error('Something is missing...')
    let childCount = 0
    let insertPosition: null | Element = null
    for (let child of parent.children) {
      if (!child.classList.contains('widget-container') || !child.classList.contains('widget-container')) continue
      if (childCount === this.state.buttonIndex && isHtmlElement(child)) {
        insertPosition = child
        break
      }
      childCount++
    }
    if (insertPosition?.previousElementSibling?.className === 'Template-footer') {
      insertPosition = insertPosition.previousElementSibling
    }

    const newId = this.idGenerator()
    const newElement = getDefaultWidget(type, newId)
    const elements = this.props.elements.concat([])
    elements.splice(this.state.buttonIndex, 0, newElement)

    const container = document.createElement('div')
    container.classList.add(
      'widget-container',
      `widget-container--${type}`,
      `${this.props.classPrefix}${newId}`,
      'widget-container--inactive'
    )

    const placeholder = document.createElement('div')
    placeholder.classList.add('widget-placeholder')
    container.appendChild(placeholder)

    if (insertPosition) {
      insertPosition.parentElement?.insertBefore(container, insertPosition)
    } else if (parent) {
      parent.appendChild(container)
    }

    if (type !== 'container_2_1' && type !== 'introduction_bar' && type !== 'statistics_bar') {
      this.setState({editingId: newId})
      publish('edit_column', {editing: true})
    }
    this.props.onChange(elements)
  }

  canMoveUp(isMovable: boolean, idx: number) {
    if (this.props.nested) {
      return idx > 0
    }
    return isMovable && idx > 2
  }

  canMoveDown(isMovable: boolean, idx: number, totalCount: number) {
    if (this.props.nested) {
      return idx < (totalCount - 1)
    }
    return isMovable && idx < totalCount - 2
  }

  setConstraints() {
    if (!this.columnContainer) return
    const isFullPage = this.props.container === '.body-wrapper'
    if (isFullPage) return
    const el = this.columnContainer
    const {left, top, right, bottom} = el.getBoundingClientRect()
    const newMinX = left + document.documentElement.scrollLeft
    const newMinY = top + document.documentElement.scrollTop
    const newMaxX = right + document.documentElement.scrollLeft
    const newMaxY = bottom + document.documentElement.scrollTop
    if (newMinY === this.minY && newMinX === this.minX &&
        newMaxY === this.maxY && newMaxX === this.maxX) return
    this.minX = newMinX
    this.minY = newMinY
    this.maxX = newMaxX
    this.maxY = newMaxY
    this.setState({x: this.state.x})
  }

  componentDidMount() {
    this.columnContainer = document.querySelector(`${this.props.container}`)
    this.setConstraints()
    window.addEventListener('resize', this.setConstraintsThrottled)
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.setConstraintsThrottled)
  }

  componentDidUpdate() {
    this.setConstraints()
  }

  newButtonToggle(isVisible: boolean) {
    this.setState({
      disableButtonHover: isVisible
    })
  }
  onEdit(el: HomepageElement) {
    this.setState({editingId: el.id}, () => publish('edit_column', {editing: true}))
  }

  onClose() {
    this.setState({editingId: null}, () => {publish('edit_column', {editing: false})})
  }

  getWidgetContainer (id: number): HTMLElement {
    let potentialContainer = this.containerCache[id]
    if (potentialContainer) return potentialContainer

    const className = `.${this.props.classPrefix}${id}`
    const widgetContainer = document.querySelector(`${this.props.container} ${className}`)
    if (!isHtmlElement(widgetContainer)) {
      throw Error(`Expected "${className}" to be in the DOM but it was not found`)
    }
    this.containerCache[id] = widgetContainer
    return widgetContainer
  }

  render({widgetList, controlUrl}: ColumnProps, {x, y, newButtonPosition, disabled}: ColumnState) {
    const xAbs = x + document.documentElement.scrollLeft
    const yAbs = y + document.documentElement.scrollTop

    let showNewButton = xAbs > this.minX - 50 && xAbs < this.maxX + 50 &&
      yAbs > this.minY - 50 && yAbs < this.maxY + 50

    return <div class="qa-widget-column">
      <Tracker onMove={(x, y) => this.setState({x, y})} />
      {
        this.props.elements.map((el, idx, {length}) => {
          const widgetContainer = this.getWidgetContainer(el.id)
          const isMovable = nonMovableElements.indexOf(el.type) < 0
          return <OrderAnimator
            isMovable={isMovable}
            idx={idx}
            id={el.id}
            key={el.id}
            into={widgetContainer}>
            <Mover
              key={el.id}
              x={this.state.x}
              y={this.state.y}
              resolvedPosition={(position, x, y, width) => {
                if (this.state.disableButtonHover) return

                if (el.type === 'footer' || el.type === 'header' || (el.type === 'navigation_bar' && position == 'top')) {
                  return this.setState({isNewButtonVisible: false})
                }
                this.setState({
                  newButtonPosition: {x, y, width},
                  isNewButtonVisible: true,
                  buttonIndex: position === 'top' ? idx : idx + 1,
                })
              }} >
              <HomepageEditor
                idx={idx}
                edit={el.id === this.state.editingId}
                isMovable={isMovable}
                controlUrl={controlUrl}
                homepageElement={el}
                totalCount={length}
                parentEl={widgetContainer}
                canMoveUp={this.canMoveUp(isMovable, idx)}
                canMoveDown={this.canMoveDown(isMovable, idx, length)}
                onEdit={this.onEdit}
                onDone={this.onDone}
                onClose={this.onClose}
                onMove={this.onMove}
                onVisibleToggle={this.onVisibleToggle}
                onRemove={this.onRemove}
                onMouseOut={this.props.onChildMouseOut}
                onMouseOver={this.props.onChildMouseOver}
                disabled={disabled}
                currentPage={this.props.currentPage}
              />
            </Mover>
          </OrderAnimator>
        })
      }
      <Portal key='new-button' into={'body'}>
        <NewWidgetButton
          widgetList={widgetList || []}
          onToggle={this.newButtonToggle}
          onWidgetChoice={this.onAddWidget}
          visible={(this.state.isNewButtonVisible || this.props.nested) && showNewButton && !disabled}
          x={newButtonPosition.x || this.minX}
          y={newButtonPosition.y || this.minY}
          width={newButtonPosition.width || 0}
        />
      </Portal>
    </div>
  }
}
